import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { setLanguage } from "@/i18n";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const LANGUAGES = [
  { code: "pt", label: "Português", short: "PT", flag: "🇵🇹" },
  { code: "en", label: "English", short: "EN", flag: "🇬🇧" },
];

/**
 * Compact language switcher. Persists to localStorage and, when authenticated,
 * to the user profile via PATCH /auth/me/language.
 * `compact` = icon-only trigger for sidebar footers. Default = shows short code.
 */
export default function LanguageSwitcher({ compact = false, className = "" }) {
  const { i18n } = useTranslation();
  const { user } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const current = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  const pick = async (code) => {
    if (code === i18n.language) { setOpen(false); return; }
    setLanguage(code);
    setOpen(false);
    if (user && user.id) {
      try { await api.patch("/auth/me/language", { language: code }); } catch (_e) { /* ignore */ }
    }
  };

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      <button
        type="button"
        data-testid="language-switcher"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded-lg text-sm text-zinc-700 hover:text-zinc-900 hover:bg-zinc-100 border border-zinc-200 ${compact ? "px-2 py-1.5" : "px-2.5 py-1.5"}`}
        aria-label="Change language"
      >
        <Globe className="w-3.5 h-3.5" />
        <span className="font-medium">{current.short}</span>
      </button>
      {open && (
        <div
          data-testid="language-switcher-menu"
          className="absolute right-0 bottom-full mb-1 z-50 min-w-[140px] bg-white border border-zinc-200 rounded-lg shadow-lg overflow-hidden"
        >
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              data-testid={`language-option-${l.code}`}
              onClick={() => pick(l.code)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-zinc-50 ${l.code === i18n.language ? "bg-zinc-50 font-semibold text-zinc-900" : "text-zinc-700"}`}
            >
              <span aria-hidden="true">{l.flag}</span>
              <span>{l.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
